import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, Search, Edit, Trash2, Eye, Loader2, Mail, Phone,
  MapPin, Calendar, GraduationCap, RefreshCw, Download, Filter,
  Check, X, BookOpen, Building2, Hash
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStudentsPage = () => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [centerFilter, setCenterFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all'); // New category filter
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      // Use new endpoint that includes center info
      const response = await axios.get(`${API}/admin/students-with-centers`);
      setStudents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      // Fallback to old endpoint
      try {
        const fallbackResponse = await axios.get(`${API}/admin/users?role=student`);
        setStudents(fallbackResponse.data || []);
      } catch (err) {
        toast.error('Failed to load students');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get unique centers for filter
  const uniqueCenters = [...new Set(students.filter(s => s.center_code).map(s => s.center_code))];

  const handleSaveStudent = async () => {
    if (!selectedStudent) return;
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedStudent.id}`, {
        name: selectedStudent.name,
        email: selectedStudent.email,
        phone: selectedStudent.phone
      });
      toast.success('Student updated successfully');
      setEditDialogOpen(false);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update student');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteStudent = async () => {
    if (!selectedStudent) return;
    try {
      await axios.delete(`${API}/admin/users/${selectedStudent.id}`);
      toast.success('Student deleted successfully');
      setDeleteConfirmOpen(false);
      setSelectedStudent(null);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.phone?.includes(searchTerm) ||
      student.center_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.center_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.course_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCenter = centerFilter === 'all' || student.center_code === centerFilter;
    
    // Category filter logic
    let matchesCategory = true;
    if (categoryFilter !== 'all') {
      const studentCategory = student.student_category || '';
      const trainingMode = student.training_mode?.toLowerCase() || '';
      const courseCategory = student.course_category?.toLowerCase() || '';
      
      if (categoryFilter === 'stt') {
        matchesCategory = studentCategory === 'stt' || (trainingMode === 'stt' && courseCategory !== 'degree' && courseCategory !== 'diploma');
      } else if (categoryFilter === 'rpl') {
        matchesCategory = studentCategory === 'rpl' || trainingMode === 'rpl';
      } else if (categoryFilter === 'distance') {
        matchesCategory = studentCategory === 'distance_education' || courseCategory === 'degree' || courseCategory === 'diploma';
      }
    }
    
    return matchesSearch && matchesCenter && matchesCategory;
  });

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Center Name', 'Center Code', 'Course', 'Registered Date'];
    const rows = filteredStudents.map(s => [
      s.name,
      s.email,
      s.phone || '',
      s.center_name || '',
      s.center_code || '',
      s.course_name || '',
      s.created_at ? new Date(s.created_at).toLocaleDateString() : ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'students.csv';
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-students-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Students Management</h1>
          <p className="text-slate-500 mt-1">{students.length} total students</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={exportToCSV} variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          <Button onClick={fetchStudents} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Students</p>
              <p className="text-xl font-bold text-slate-900">{students.length}</p>
            </div>
          </div>
        </div>
        {/* STT Count */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors" onClick={() => setCategoryFilter('stt')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">STT</p>
              <p className="text-xl font-bold text-emerald-600">
                {students.filter(s => s.student_category === 'stt' || (s.training_mode === 'STT' && s.course_category !== 'degree' && s.course_category !== 'diploma')).length}
              </p>
            </div>
          </div>
        </div>
        {/* RPL Count */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 cursor-pointer hover:border-amber-300 transition-colors" onClick={() => setCategoryFilter('rpl')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Check className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">RPL</p>
              <p className="text-xl font-bold text-amber-600">
                {students.filter(s => s.student_category === 'rpl' || s.training_mode === 'RPL').length}
              </p>
            </div>
          </div>
        </div>
        {/* Distance Education Count */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 cursor-pointer hover:border-purple-300 transition-colors" onClick={() => setCategoryFilter('distance')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Distance Ed.</p>
              <p className="text-xl font-bold text-purple-600">
                {students.filter(s => s.student_category === 'distance_education' || s.course_category === 'degree' || s.course_category === 'diploma').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">This Month</p>
              <p className="text-xl font-bold text-slate-900">
                {students.filter(s => {
                  if (!s.created_at) return false;
                  const date = new Date(s.created_at);
                  const now = new Date();
                  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Verified</p>
              <p className="text-xl font-bold text-slate-900">
                {students.filter(s => s.is_verified).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search name, email, phone, center, course..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        {/* Category Filter - STT, RPL, Distance Education */}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white min-w-[180px]"
        >
          <option value="all">All Categories</option>
          <option value="stt">STT (Skill Training)</option>
          <option value="rpl">RPL (Assessment)</option>
          <option value="distance">Distance Education</option>
        </select>
        
        {uniqueCenters.length > 0 && (
          <select
            value={centerFilter}
            onChange={(e) => setCenterFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white min-w-[180px]"
          >
            <option value="all">All Centers</option>
            {uniqueCenters.map(code => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        )}
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Center</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Registered</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No students found
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                          <span className="text-blue-600 font-medium">{student.name?.charAt(0) || 'S'}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{student.name}</p>
                          <p className="text-sm text-slate-500 truncate">{student.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {student.center_code ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3 text-purple-500" />
                            <span className="text-sm font-medium text-purple-700">{student.center_code}</span>
                          </div>
                          {student.center_name && (
                            <p className="text-xs text-slate-500 truncate max-w-[150px]" title={student.center_name}>
                              {student.center_name}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Direct</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {student.course_name ? (
                        <div className="flex items-center gap-1">
                          <GraduationCap className="w-3 h-3 text-emerald-500" />
                          <span className="text-sm text-slate-700 truncate max-w-[120px]" title={student.course_name}>
                            {student.course_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {student.phone && (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Phone className="w-3 h-3" />
                            {student.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Calendar className="w-3 h-3" />
                        {student.created_at ? new Date(student.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        student.is_verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {student.is_verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedStudent(student); setViewDialogOpen(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedStudent(student); setEditDialogOpen(true); }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => { setSelectedStudent(student); setDeleteConfirmOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Student Details / छात्र विवरण
            </DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-2xl">{selectedStudent.name?.charAt(0) || 'S'}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{selectedStudent.name}</h3>
                  <p className="text-slate-500">{selectedStudent.email}</p>
                </div>
              </div>
              
              {/* Center & Course Info */}
              {(selectedStudent.center_code || selectedStudent.course_name) && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                  <h4 className="text-sm font-medium text-purple-800 mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Center & Course Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-purple-600">Center Code</p>
                      <p className="font-medium text-purple-900">{selectedStudent.center_code || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-600">Center Name</p>
                      <p className="font-medium text-purple-900">{selectedStudent.center_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-600">Course</p>
                      <p className="font-medium text-purple-900">{selectedStudent.course_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-600">Batch</p>
                      <p className="font-medium text-purple-900">{selectedStudent.batch_name || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{selectedStudent.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <p className="font-medium">{selectedStudent.is_verified ? 'Verified' : 'Pending'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Registered</p>
                  <p className="font-medium">
                    {selectedStudent.created_at ? new Date(selectedStudent.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Enrollments</p>
                  <p className="font-medium">{selectedStudent.enrollments?.length || 0} courses</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              Edit Student
            </DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={selectedStudent.name || ''}
                  onChange={(e) => setSelectedStudent({ ...selectedStudent, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={selectedStudent.email || ''}
                  onChange={(e) => setSelectedStudent({ ...selectedStudent, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={selectedStudent.phone || ''}
                  onChange={(e) => setSelectedStudent({ ...selectedStudent, phone: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveStudent} disabled={isSaving} className="bg-amber-600 hover:bg-amber-700">
                  {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">Are you sure you want to delete this student?</p>
            <p className="text-red-500 text-sm mt-2">Student: {selectedStudent?.name}</p>
            <p className="text-slate-400 text-xs mt-2">This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDeleteStudent} className="bg-red-600 hover:bg-red-700">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStudentsPage;
